import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, RotateCcw, Trash2 } from "lucide-react";
import ABPlayer from "@/components/voicesongs/ABPlayer";
import { VOICE_SONG_ACTIVE, VOICE_SONG_PHASE, deleteVoiceSong, retryVoiceSong } from "@/lib/voiceSongsApi";
import { safeApiError } from "@/lib/safeErrorText";

function JobRow({ job, onChanged }) {
  const busy = VOICE_SONG_ACTIVE.includes(job.status);
  const remove = async () => { try { await deleteVoiceSong(job.id); onChanged(); } catch (e) { toast.error("Couldn't remove", { description: safeApiError(e) }); } };
  const retry = async () => { try { await retryVoiceSong(job.id); onChanged(); } catch (e) { toast.error("Couldn't restart", { description: safeApiError(e) }); } };
  return (
    <li data-testid={`voice-song-${job.id}`} className="rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-[15px] font-semibold">{job.title}</div>
          <div className="truncate text-[11px] text-white/45">{job.source?.kind === "upload" ? `Uploaded · ${job.source.label}` : "From your library"} · {job.voice?.name} v{job.voice?.version_n}{job.pitch ? ` · ${job.pitch > 0 ? "+" : ""}${job.pitch} st` : ""}</div>
        </div>
        {!busy ? <button type="button" data-testid={`voice-song-delete-${job.id}`} aria-label="Remove from this list" onClick={remove} className="grid h-8 w-8 place-items-center rounded-full text-white/40 hover:bg-rose-500/15 hover:text-rose-200"><Trash2 className="h-3.5 w-3.5" /></button> : null}
      </div>
      {busy ? <p data-testid={`voice-song-progress-${job.id}`} className="mt-2 flex items-center gap-2 text-[13px] text-amber-100"><Loader2 className="h-4 w-4 animate-spin" />{VOICE_SONG_PHASE[job.phase] || "Working"}…</p> : null}
      {job.error ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p data-testid={`voice-song-error-${job.id}`} className="text-[13px] text-rose-200">{job.error}</p>
          <button type="button" data-testid={`voice-song-retry-${job.id}`} onClick={retry} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-[12px] text-white/80 hover:bg-white/10"><RotateCcw className="h-3.5 w-3.5" /> Try again</button>
        </div>
      ) : null}
      {job.status === "done" && job.result ? (
        <>
          <ABPlayer testId={`voice-song-ab-${job.id}`} a={{ label: "Original", src: job.original_url }} b={{ label: "My voice", src: job.result.audio_url }} />
          <Link to={`/create/song/${job.result.song_id}`} data-testid={`voice-song-open-${job.id}`} className="mt-3 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-cyan-200 hover:text-white">
            Open the new song (mix, master, export) <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </>
      ) : null}
    </li>
  );
}

/** Songs being sung — or already sung — in your voice, with Original ↔ My voice compare. */
export default function VoiceSongJobs({ rows, onChanged, testId = "voice-song-jobs" }) {
  if (!rows?.length) return null;
  return <ul data-testid={testId} className="mt-4 space-y-3">{rows.map((j) => <JobRow key={j.id} job={j} onChanged={onChanged} />)}</ul>;
}
